/** 高德原生经纬度对象的最小结构。 */
export type AMapNativeLngLat = {
  getLng: () => number;
  getLat: () => number;
};

/** 高德原生边界对象的最小结构。 */
export type AMapNativeBounds = {
  getSouthWest: () => AMapNativeLngLat;
  getNorthEast: () => AMapNativeLngLat;
};

/** 高德原生地图对象的最小结构。 */
export type AMapNativeMap = {
  addControl: (control: object) => void;
  removeControl: (control: object) => void;
  getBounds: () => AMapNativeBounds;
  getZoom: () => number;
  setZoomAndCenter: (zoom: number, center: [number, number]) => void;
  on: (event: "moveend" | "zoomend", handler: () => void) => void;
  off: (event: "moveend" | "zoomend", handler: () => void) => void;
  add: (markers: AMapNativeMarker[]) => void;
  remove: (markers: AMapNativeMarker[]) => void;
  destroy: () => void;
};

/** 高德原生 Marker 对象的最小结构。 */
export type AMapNativeMarker = {
  on: (event: "click", handler: () => void) => void;
  off: (event: "click", handler: () => void) => void;
};

/** 高德原生 Marker 构造选项。 */
export type AMapNativeMarkerOptions = {
  position: [number, number];
  content?: HTMLElement;
  anchor?: "center";
  zIndex?: number;
};

/** 高德原生 InfoWindow 对象的最小结构。 */
export type AMapNativeInfoWindow = {
  open: (map: AMapNativeMap, position: [number, number]) => void;
  close: () => void;
};

/** 高德原生 CitySearch 对象的最小结构。 */
export type AMapNativeCitySearch = {
  getLocalCity: (callback: (status: string, result: unknown) => void) => void;
};

/** 高德原生 Geolocation 对象的最小结构。 */
export type AMapNativeGeolocation = {
  getCurrentPosition: (
    callback: (status: string, result: unknown) => void,
  ) => void;
};

/** 高德 Geolocation 控件选项。 */
export type AMapNativeGeolocationOptions = {
  enableHighAccuracy: boolean;
  timeout: number;
  maximumAge: number;
  showButton: boolean;
  showMarker: boolean;
  showCircle: boolean;
  panToLocation: boolean;
  zoomToAccuracy: boolean;
};

/** 酒店地图使用的最小高德 JS API 命名空间。 */
export type AMapNamespace = {
  Map: new (
    container: HTMLElement,
    options: { center: [number, number]; zoom: number },
  ) => AMapNativeMap;
  Scale: new () => object;
  ToolBar: new () => object;
  Marker: new (options: AMapNativeMarkerOptions) => AMapNativeMarker;
  InfoWindow: new (options: { content: HTMLElement }) => AMapNativeInfoWindow;
  CitySearch: new () => AMapNativeCitySearch;
  Geolocation: new (options: AMapNativeGeolocationOptions) => AMapNativeGeolocation;
};

/** 页面全局可能处于插件尚未补载完成的高德命名空间。 */
export type AMapGlobal = Partial<AMapNamespace> & {
  plugin?: (plugins: string[], callback: () => void) => void;
};

/** 高德 JS API 加载参数。 */
export type LoadAmapOptions = {
  key: string;
  securityCode?: string;
};

declare global {
  interface Window {
    AMap?: AMapGlobal;
    _AMapSecurityConfig?: {
      securityJsCode?: string;
    };
  }
}

const AMAP_SCRIPT_SELECTOR = "script[data-hotel-map-amap]";
const AMAP_PLUGIN = "AMap.Scale,AMap.ToolBar,AMap.CitySearch,AMap.Geolocation";
const REQUIRED_AMAP_PLUGINS = [
  "AMap.Scale",
  "AMap.ToolBar",
  "AMap.Marker",
  "AMap.InfoWindow",
  "AMap.CitySearch",
  "AMap.Geolocation",
];

type AmapLoadAttempt = {
  token: symbol;
  script: HTMLScriptElement | null;
  promise: Promise<AMapNamespace>;
  resolve: (namespace: AMapNamespace) => void;
  reject: (error: Error) => void;
  loadListener: EventListener | null;
  errorListener: EventListener | null;
  settled: boolean;
};

let amapLoadPromise: Promise<AMapNamespace> | null = null;
let activeAttempt: AmapLoadAttempt | null = null;

/** 单例加载酒店地图使用的高德 JS API，并在失败后允许重新加载。 */
export function loadAmap(options: LoadAmapOptions): Promise<AMapNamespace> {
  if (amapLoadPromise) {
    return amapLoadPromise;
  }

  if (isCompleteAmapNamespace(window.AMap)) {
    return Promise.resolve(window.AMap);
  }
  if (window.AMap) {
    if (!canLoadAmapPlugins(window.AMap)) {
      return Promise.reject(new Error("高德地图对象未初始化"));
    }
    const attempt = beginAttempt(null);
    ensureAmapReady(attempt);
    return attempt.promise;
  }

  const key = options.key.trim();
  if (!key) {
    return Promise.reject(new Error("未配置 VITE_AMAP_JS_API_KEY"));
  }

  setSecurityConfig(options.securityCode);
  const existingScript = document.querySelector<HTMLScriptElement>(AMAP_SCRIPT_SELECTOR);
  const script = existingScript ?? createAmapScript(key);
  const attempt = beginAttempt(script);
  if (!existingScript) {
    document.head.append(script);
  }
  return attempt.promise;
}

/** 创建带固定插件参数的 feature 专用脚本。 */
function createAmapScript(key: string): HTMLScriptElement {
  const script = document.createElement("script");
  script.dataset.hotelMapAmap = "";
  script.async = true;
  script.src = `https://webapi.amap.com/maps?v=2.0&key=${encodeURIComponent(key)}&plugin=${encodeURIComponent(AMAP_PLUGIN)}`;
  return script;
}

/** 创建并登记唯一加载 attempt。 */
function beginAttempt(script: HTMLScriptElement | null): AmapLoadAttempt {
  let resolvePromise: (namespace: AMapNamespace) => void = () => undefined;
  let rejectPromise: (error: Error) => void = () => undefined;
  const promise = new Promise<AMapNamespace>((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });
  const attempt: AmapLoadAttempt = {
    token: Symbol("hotel-map-amap-attempt"),
    script,
    promise,
    resolve: resolvePromise,
    reject: rejectPromise,
    loadListener: null,
    errorListener: null,
    settled: false,
  };
  activeAttempt = attempt;
  amapLoadPromise = promise;

  if (script) {
    attempt.loadListener = () => ensureAmapReady(attempt);
    attempt.errorListener = () => settleFailure(attempt, "高德地图脚本加载失败");
    script.addEventListener("load", attempt.loadListener);
    script.addEventListener("error", attempt.errorListener);
  }
  return attempt;
}

/** 校验命名空间，必要时通过高德 plugin API 补载构造器。 */
function ensureAmapReady(attempt: AmapLoadAttempt): void {
  if (!isCurrentAttempt(attempt)) {
    return;
  }
  const candidate = window.AMap;
  if (isCompleteAmapNamespace(candidate)) {
    settleSuccess(attempt, candidate);
    return;
  }
  if (!canLoadAmapPlugins(candidate)) {
    settleFailure(attempt, "高德地图对象未初始化");
    return;
  }

  try {
    candidate.plugin([...REQUIRED_AMAP_PLUGINS], () => {
      if (!isCurrentAttempt(attempt)) {
        return;
      }
      if (!isCompleteAmapNamespace(window.AMap)) {
        settleFailure(attempt, "高德地图对象未初始化");
        return;
      }
      settleSuccess(attempt, window.AMap);
    });
  } catch {
    settleFailure(attempt, "高德地图对象未初始化");
  }
}

/** 成功结束当前 attempt 并清理脚本事件处理器。 */
function settleSuccess(attempt: AmapLoadAttempt, namespace: AMapNamespace): void {
  if (!isCurrentAttempt(attempt)) {
    return;
  }
  attempt.settled = true;
  removeAttemptListeners(attempt);
  activeAttempt = null;
  amapLoadPromise = null;
  attempt.resolve(namespace);
}

/** 失败结束当前 attempt，且只移除该 attempt 自己的脚本。 */
function settleFailure(attempt: AmapLoadAttempt, message: string): void {
  if (!isCurrentAttempt(attempt)) {
    return;
  }
  attempt.settled = true;
  removeAttemptListeners(attempt);
  attempt.script?.remove();
  activeAttempt = null;
  amapLoadPromise = null;
  attempt.reject(new Error(message));
}

/** 判断 attempt 是否仍为当前唯一加载任务。 */
function isCurrentAttempt(attempt: AmapLoadAttempt): boolean {
  return !attempt.settled
    && activeAttempt === attempt
    && activeAttempt.token === attempt.token;
}

/** 只移除当前 attempt 自己注册的脚本事件监听器。 */
function removeAttemptListeners(attempt: AmapLoadAttempt): void {
  if (!attempt.script) {
    return;
  }
  if (attempt.loadListener) {
    attempt.script.removeEventListener("load", attempt.loadListener);
    attempt.loadListener = null;
  }
  if (attempt.errorListener) {
    attempt.script.removeEventListener("error", attempt.errorListener);
    attempt.errorListener = null;
  }
}

/** 判断未知全局对象是否具备 adapter 所需的全部构造器。 */
function isCompleteAmapNamespace(value: unknown): value is AMapNamespace {
  if (!isRecord(value)) {
    return false;
  }
  return ["Map", "Scale", "ToolBar", "Marker", "InfoWindow", "CitySearch", "Geolocation"]
    .every((name) => typeof value[name] === "function");
}

/** 判断现有高德对象能否通过 plugin API 补全构造器。 */
function canLoadAmapPlugins(value: unknown): value is AMapGlobal & {
  Map: AMapNamespace["Map"];
  plugin: NonNullable<AMapGlobal["plugin"]>;
} {
  return isRecord(value)
    && typeof value.Map === "function"
    && typeof value.plugin === "function";
}

/** 在脚本插入前设置安全码，空值会移除上一轮配置。 */
function setSecurityConfig(securityCode?: string): void {
  const normalizedSecurityCode = securityCode?.trim();
  if (!normalizedSecurityCode) {
    delete window._AMapSecurityConfig;
    return;
  }
  window._AMapSecurityConfig = { securityJsCode: normalizedSecurityCode };
}

/** 判断未知值是否为可读取字段的对象。 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
